'use client';

import { useDialogStore } from 'stores/dialog';
import { Dialog, DIALOG_TYPE } from '../dialog';
import { OrderWayCollection } from '../orderWayCollection';
import type { CardNode, Device, ThemeNode } from 'data-layer';
import { LINK_TYPES, THEME_DEFAULT_PROPS } from 'data-layer/constants';
import { isLinkType } from 'utils';

interface OrderWaysDialogProps {
  headline?: string;
  orderWays: CardNode[];
  theme?: ThemeNode;
  layout?: string;
  device: Device;
}

/**
 * Replaces the static /online-designer placeholder URL with the product specific editor URL
 * from the clicked magazine product.
 */
function injectEditorUrl(cards: CardNode[], editorUrl: string): CardNode[] {
  return cards.map(card => {
    const hasOnlineDesignerLink = card.links?.some(link => isLinkType(link, LINK_TYPES.ONLINE_DESIGNER));

    if (!hasOnlineDesignerLink) {
      return card;
    }

    const updatedLinks = card.links?.map(link =>
      isLinkType(link, LINK_TYPES.ONLINE_DESIGNER) ? { ...link, url: editorUrl, masked: true } : link
    );

    return {
      ...card,
      links: updatedLinks
    };
  });
}

/**
 * OrderWaysDialog Component
 *
 * This component renders a dialog containing order-way options (Online Designer, Software, App, etc.)
 * when triggered by buttons with the ORDER_WAYS_PRODUCT link type.
 *
 * Props:
 * - orderWaysArticle: The article containing order-way cards to display
 * - device: Current device type (mobile/desktop)
 *
 * The dialog is controlled by Zustand store and displays the order-ways
 * in a special layout with a primary (focused) option and secondary options.
 */
export function OrderWaysDialog({ headline, orderWays, theme, layout, device }: OrderWaysDialogProps) {
  const isOpen = useDialogStore(state => state.isOpen);
  const closeDialog = useDialogStore(state => state.closeDialog);
  const dialogOptions = useDialogStore(state => state.options);

  // Only show dialog if it's open and the type matches order-ways
  if (!isOpen || !dialogOptions || dialogOptions.type !== 'order-ways-product') {
    return null;
  }

  if (!orderWays || orderWays.length === 0) {
    return null;
  }

  // Append the editor URL to the order-ways link
  const resolvedOrderWays = dialogOptions.url
    ? injectEditorUrl(orderWays, dialogOptions.url)
    : orderWays;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeDialog}
      headline={headline}
      dialogType={DIALOG_TYPE.ORDER_WAYS}
      fullScreen={device === 'mobile'}
    >
      <OrderWayCollection
        orderWays={resolvedOrderWays}
        theme={theme || THEME_DEFAULT_PROPS}
        layout={layout || ''}
        device={device}
        inDialog={true}
      />
    </Dialog>
  );
}
